import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Search, Printer, Download, RefreshCw, X,
  ChevronLeft, ChevronRight, CheckCircle2, AlertTriangle, XCircle, Loader2, ClipboardCheck, Eye,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const PAGE_SIZE = 25;

const statusMeta = (s) => {
  switch (s) {
    case 'completed': return { label: 'Completed', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' };
    case 'completed_with_errors': return { label: 'Completed w/ errors', cls: 'bg-amber-100 text-amber-700 border-amber-200' };
    case 'running': return { label: 'Running', cls: 'bg-blue-100 text-blue-700 border-blue-200' };
    case 'queued': return { label: 'Queued', cls: 'bg-slate-100 text-slate-600 border-slate-200' };
    case 'failed': return { label: 'Failed', cls: 'bg-red-100 text-red-700 border-red-200' };
    default: return { label: s || '—', cls: 'bg-gray-100 text-gray-600 border-gray-200' };
  }
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso);
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const ReconcileReport = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Per-job detailed bundle breakdown (opened in a dialog).
  const [detailJob, setDetailJob] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const openDetail = async (job) => {
    setDetailJob(job);
    setDetail(null);
    setDetailLoading(true);
    try {
      const { data } = await apiClient.get(`/sections/greige/reconcile/report-full/${job.job_id}/detail`);
      setDetail(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load bundle details');
      setDetail({ rows: [] });
    } finally {
      setDetailLoading(false);
    }
  };

  const exportDetailCsv = async () => {
    if (!detailJob) return;
    try {
      const r = await apiClient.get(`/sections/greige/reconcile/report-full/${detailJob.job_id}/detail`, {
        params: { format: 'csv' }, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `reconcile_detail_${detailJob.document_barcode || detailJob.job_id}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Detail export failed');
    }
  };

  const fetchReport = useCallback(async (overrides = {}) => {
    const f = {
      page: overrides.page ?? page,
      search: overrides.search ?? search,
      date_from: overrides.date_from ?? dateFrom,
      date_to: overrides.date_to ?? dateTo,
    };
    setLoading(true);
    try {
      const params = { page: f.page, page_size: PAGE_SIZE };
      if (f.search) params.search = f.search;
      if (f.date_from) params.date_from = f.date_from;
      if (f.date_to) params.date_to = f.date_to;
      const { data } = await apiClient.get('/sections/greige/reconcile/report-full', { params });
      setRows(data.rows || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
      setPage(data.page || 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load reconcile report');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [page, search, dateFrom, dateTo]);

  useEffect(() => { fetchReport({ page: 1 }); /* eslint-disable-next-line */ }, []);

  const applyFilters = () => fetchReport({ page: 1 });
  const clearFilters = () => {
    setSearch(''); setDateFrom(''); setDateTo('');
    fetchReport({ page: 1, search: '', date_from: '', date_to: '' });
  };
  const goPage = (p) => { if (p >= 1 && p <= totalPages) fetchReport({ page: p }); };

  const handleExport = async () => {
    setExporting(true);
    try {
      const r = await apiClient.get('/sections/greige/reconcile/report-full', {
        params: { search: search || undefined, date_from: dateFrom || undefined, date_to: dateTo || undefined, format: 'csv' },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `reconcile_report_${new Date().toISOString().slice(0, 16).replace(/[:T]/g, '')}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV exported');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const printReport = () => {
    const win = window.open('', '_blank');
    if (!win) return;
    const body = rows.map((r, i) => `<tr>
        <td>${i + 1}</td>
        <td>${fmtDate(r.date)}</td>
        <td>${r.document_barcode || '—'}</td>
        <td>${r.vendor_doc_no || '—'}</td>
        <td>${r.vendor_name || '—'}</td>
        <td>${statusMeta(r.status).label}</td>
        <td style="text-align:center">${r.processed_bundles}/${r.total_bundles}</td>
        <td style="text-align:right">${r.assigned_usns || r.reconciled_qty || 0}</td>
        <td style="text-align:right">${r.total_qty}</td>
        <td style="text-align:right">${r.marker_total ?? 0}</td>
        <td style="text-align:right">${r.sort_total ?? 0}</td>
        <td style="text-align:right">${(r.qty_difference_total || 0) > 0 ? `+${r.qty_difference_total}` : (r.qty_difference_total || 0)}</td>
        <td>${r.staff_name || '—'}</td>
        <td style="text-align:center">${r.errors_count || 0}</td>
      </tr>`).join('');
    win.document.write(`<!doctype html><html><head><title>Reconcile Report</title>
      <style>
        body{font-family:Arial,sans-serif;padding:24px;color:#111}
        h1{font-size:18px;margin:0 0 4px}
        .meta{font-size:12px;color:#555;margin-bottom:14px}
        table{width:100%;border-collapse:collapse;font-size:12px}
        th,td{border:1px solid #ccc;padding:6px 8px;text-align:left}
        th{background:#f3f4f6}
      </style></head><body>
      <h1>Greige Reconcile Report</h1>
      <div class="meta">Generated ${new Date().toLocaleString('en-GB')} · ${total} reconciliation(s)${(dateFrom || dateTo) ? ` · ${dateFrom || '…'} to ${dateTo || '…'}` : ''}${search ? ` · search "${search}"` : ''}</div>
      <table><thead><tr>
        <th>#</th><th>Date</th><th>Document</th><th>Vendor Doc No</th><th>Vendor Name</th><th>Status</th>
        <th style="text-align:center">Bundles</th><th style="text-align:right">USNs / Qty</th>
        <th style="text-align:right">Target Qty</th><th style="text-align:right">Marker Total</th>
        <th style="text-align:right">Sort Total</th><th style="text-align:right">Difference</th>
        <th>Staff</th><th style="text-align:center">Errors</th>
      </tr></thead><tbody>${body}</tbody></table></body></html>`);
    win.document.close(); win.focus(); win.print();
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5] p-4 md:p-6" data-testid="reconcile-report-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/sections/greige')} data-testid="rr-back-btn">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-gray-900 flex items-center gap-2">
                <ClipboardCheck className="h-6 w-6 text-violet-600" /> Reconcile Report
              </h1>
              <p className="text-sm text-gray-500">Greige reconciliations — date &amp; global filters</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={handleExport} disabled={exporting || !rows.length}
              className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="rr-export-csv-btn">
              <Download className="h-4 w-4 mr-2" /> {exporting ? 'Exporting…' : 'Export CSV'}
            </Button>
            <Button onClick={printReport} variant="outline" disabled={!rows.length} data-testid="rr-print-btn">
              <Printer className="h-4 w-4 mr-2" /> Print
            </Button>
          </div>
        </div>

        <Card className="mb-4"><CardContent className="p-4">
          <div className="flex flex-col md:flex-row md:items-end gap-3">
            <div className="flex-1">
              <label className="text-xs font-medium text-gray-600">Search (document / vendor / staff / status)</label>
              <Input value={search} onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
                placeholder="Search…" data-testid="rr-search-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">From</label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="rr-date-from" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">To</label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="rr-date-to" />
            </div>
            <div className="flex gap-2">
              <Button onClick={applyFilters} className="bg-violet-600 hover:bg-violet-700" data-testid="rr-apply-btn"><Search className="h-4 w-4 mr-1" /> Apply</Button>
              <Button variant="outline" onClick={clearFilters} data-testid="rr-clear-btn"><X className="h-4 w-4 mr-1" /> Clear</Button>
              <Button variant="ghost" size="icon" onClick={() => fetchReport()} data-testid="rr-refresh-btn"><RefreshCw className="h-4 w-4" /></Button>
            </div>
          </div>
          <div className="mt-3 text-sm text-gray-500" data-testid="rr-total">Total reconciliations: <b className="text-gray-800">{total}</b></div>
        </CardContent></Card>

        <Card><CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Document</TableHead>
                <TableHead>Vendor Doc No</TableHead>
                <TableHead>Vendor Name</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-center">Bundles</TableHead>
                <TableHead className="text-right">USNs / Qty</TableHead>
                <TableHead className="text-right">Target Qty</TableHead>
                <TableHead className="text-right">Marker Total</TableHead>
                <TableHead className="text-right">Sort Total</TableHead>
                <TableHead className="text-right">Difference</TableHead>
                <TableHead>Staff</TableHead>
                <TableHead className="text-center">Errors</TableHead>
                <TableHead className="text-center">Detail</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={14} className="text-center py-10 text-gray-500"><Loader2 className="h-5 w-5 animate-spin inline mr-2" />Loading…</TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={14} className="text-center py-10 text-gray-500" data-testid="rr-empty">No reconciliations found</TableCell></TableRow>
              ) : rows.map((r, i) => (
                <TableRow key={r.job_id || i} data-testid={`rr-row-${i}`}>
                  <TableCell className="text-sm whitespace-nowrap">{fmtDate(r.date)}</TableCell>
                  <TableCell className="font-mono text-sm" data-testid={`rr-doc-${i}`}>{r.document_barcode || '—'}</TableCell>
                  <TableCell className="font-mono text-sm" data-testid={`rr-vendor-doc-${i}`}>{r.vendor_doc_no || '—'}</TableCell>
                  <TableCell className="text-sm" data-testid={`rr-vendor-name-${i}`}>{r.vendor_name || '—'}</TableCell>
                  <TableCell><Badge className={statusMeta(r.status).cls}>{statusMeta(r.status).label}</Badge></TableCell>
                  <TableCell className="text-center text-sm">{r.processed_bundles}/{r.total_bundles}</TableCell>
                  <TableCell className="text-right font-semibold text-violet-700" data-testid={`rr-usns-${i}`}>{r.assigned_usns || r.reconciled_qty || 0}</TableCell>
                  <TableCell className="text-right text-sm">{r.total_qty}</TableCell>
                  <TableCell className="text-right text-sm font-semibold" data-testid={`rr-marker-total-${i}`}>{r.marker_total ?? 0}</TableCell>
                  <TableCell className="text-right text-sm font-semibold" data-testid={`rr-sort-total-${i}`}>{r.sort_total ?? 0}</TableCell>
                  <TableCell className={`text-right text-sm font-semibold ${(r.qty_difference_total || 0) !== 0 ? 'text-amber-700' : 'text-gray-400'}`} data-testid={`rr-difference-${i}`}>
                    {(r.qty_difference_total || 0) > 0 ? `+${r.qty_difference_total}` : (r.qty_difference_total || 0)}
                  </TableCell>
                  <TableCell className="text-sm">{r.staff_name || '—'}</TableCell>
                  <TableCell className="text-center">
                    {r.errors_count ? <Badge className="bg-amber-100 text-amber-700 border-amber-200">{r.errors_count}</Badge> : <span className="text-gray-300">0</span>}
                  </TableCell>
                  <TableCell className="text-center">
                    <Button variant="outline" size="sm" className="h-7 px-2 border-violet-300 text-violet-700 hover:bg-violet-50"
                      onClick={() => openDetail(r)} data-testid={`rr-view-detail-${i}`}>
                      <Eye className="h-3.5 w-3.5 mr-1" /> View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent></Card>

        <div className="flex items-center justify-between mt-4">
          <div className="text-sm text-gray-500" data-testid="rr-page-info">Page {page} of {totalPages}</div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => goPage(page - 1)} disabled={page <= 1} data-testid="rr-prev-btn"><ChevronLeft className="h-4 w-4" /> Prev</Button>
            <Button variant="outline" size="sm" onClick={() => goPage(page + 1)} disabled={page >= totalPages} data-testid="rr-next-btn">Next <ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
      </div>

      {/* Detailed per-bundle breakdown */}
      <Dialog open={!!detailJob} onOpenChange={(o) => { if (!o) { setDetailJob(null); setDetail(null); } }}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="rr-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardCheck className="h-5 w-5 text-violet-600" />
              Reconcile detail — <span className="font-mono">{detailJob?.document_barcode || '—'}</span>
            </DialogTitle>
          </DialogHeader>

          {detailLoading ? (
            <div className="py-16 text-center text-gray-500"><Loader2 className="h-6 w-6 animate-spin inline mr-2" />Loading bundle breakdown…</div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm text-gray-600 border-b pb-3">
                <span>Bundles: <b className="text-gray-900">{detail?.total || 0}</b></span>
                <span>Marker total: <b className="text-gray-900">{detail?.marker_total ?? 0}</b></span>
                <span>Sort total: <b className="text-gray-900">{detail?.sort_total ?? 0}</b></span>
                <span>Difference: <b className={`${(detail?.qty_difference_total || 0) !== 0 ? 'text-amber-700' : 'text-gray-900'}`}>{detail?.qty_difference_total ?? 0}</b></span>
                <span>USNs / Qty: <b className="text-violet-700">{detail?.usn_total || detail?.reconciled_total || 0}</b></span>
                <Button size="sm" variant="outline" className="ml-auto h-7" onClick={exportDetailCsv} disabled={!detail?.rows?.length} data-testid="rr-detail-export-btn">
                  <Download className="h-3.5 w-3.5 mr-1" /> CSV
                </Button>
              </div>

              <div className="overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Bundle Code</TableHead>
                      <TableHead>Sort No</TableHead>
                      <TableHead>Colour</TableHead>
                      <TableHead className="text-right">Sort No Qty</TableHead>
                      <TableHead className="text-right">Marker Qty</TableHead>
                      <TableHead className="text-right">Difference</TableHead>
                      <TableHead className="text-right">USNs / Qty</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(detail?.rows || []).length === 0 ? (
                      <TableRow><TableCell colSpan={7} className="text-center py-10 text-gray-500" data-testid="rr-detail-empty">No bundle detail available for this reconcile.</TableCell></TableRow>
                    ) : (detail?.rows || []).map((b, i) => {
                      const diff = b.qty_difference ?? ((b.marker_qty || 0) - (b.sort_qty || 0));
                      return (
                        <TableRow key={b.bundle_barcode || i} data-testid={`rr-detail-row-${i}`}>
                          <TableCell className="font-mono text-sm" data-testid={`rr-detail-bundle-${i}`}>{b.bundle_barcode || '—'}</TableCell>
                          <TableCell className="font-mono text-sm">{b.sort_no || '—'}</TableCell>
                          <TableCell className="text-sm">{b.color_name || '—'}</TableCell>
                          <TableCell className="text-right text-sm">{b.sort_qty ?? 0}</TableCell>
                          <TableCell className="text-right text-sm font-semibold">{b.marker_qty ?? 0}</TableCell>
                          <TableCell className={`text-right text-sm font-semibold ${diff !== 0 ? 'text-amber-700' : 'text-gray-400'}`} data-testid={`rr-detail-diff-${i}`}>
                            {diff > 0 ? `+${diff}` : diff}
                          </TableCell>
                          <TableCell className="text-right text-sm text-violet-700 font-medium">{b.assigned_usns || b.reconciled_qty || 0}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                {(detail?.rows || []).some((b) => b.error) && (
                  <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                    {(detail?.rows || []).filter((b) => b.error).map((b, i) => (
                      <div key={i}>⚠ {b.error}</div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReconcileReport;
